import * as React from 'react'
import { RefreshCw, RotateCcw, ShieldAlert, Trash2 } from 'lucide-react'
import {
  useAdminHealth, useDeadLetters, useReplayDeadLetter,
  usePurgeDomains, usePurgePreview, type PurgeScope,
} from '@/lib/queries'
import { ApiError } from '@/lib/api'
import { RateLimitCalibration } from '@/features/rate-limit/Calibration'
import { PageHeader } from '@/components/layout/AppShell'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Checkbox, Label, Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/misc'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Tooltip } from '@/components/ui/tooltip'
import { EmptyState, ErrorState, LoadingState } from '@/components/ui/states'
import { useToast } from '@/components/ui/toast'
import { Input } from '@/components/ui/input'
import { cn, formatDuration, formatNumber } from '@/lib/utils'

export default function AdminPage() {
  return (
    <>
      <PageHeader
        title="系统运维"
        description="队列健康、调度层级、熔断状态与死信重放。高风险操作会写入审计日志。"
      />
      <Tabs defaultValue="health">
        <TabsList>
          <TabsTrigger value="health">健康状态</TabsTrigger>
          <TabsTrigger value="dlq">死信队列</TabsTrigger>
          <TabsTrigger value="data">数据维护</TabsTrigger>
          <TabsTrigger value="limits">限速校准</TabsTrigger>
        </TabsList>
        <TabsContent value="health"><Health /></TabsContent>
        <TabsContent value="dlq"><DeadLetters /></TabsContent>
        <TabsContent value="data"><PurgeDomains /></TabsContent>
        <TabsContent value="limits"><RateLimitCalibration /></TabsContent>
      </Tabs>
    </>
  )
}

function Health() {
  const { data, isLoading, error, refetch, isFetching } = useAdminHealth(true)

  if (error) return <ErrorState error={error} onRetry={() => void refetch()} />
  if (isLoading) return <LoadingState rows={6} />
  if (!data) return null

  return (
    <div className="space-y-4">
      {data.backpressure.pause_low_value_scans && (
        <div role="alert" className="rounded-lg border border-amber-500/40 bg-amber-500/5 p-3">
          <p className="text-sm font-medium text-amber-900 dark:text-amber-300">背压已生效：低价值扫描已暂停</p>
          <ul className="mt-1 list-inside list-disc text-xs text-amber-800 dark:text-amber-400">
            {data.backpressure.reasons?.map((r) => <li key={r}>{r}</li>)}
          </ul>
        </div>
      )}

      {data.cost_reconciliation_issues > 0 && (
        <div role="alert" className="rounded-lg border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
          共享成本对账存在 {data.cost_reconciliation_issues} 条差异，需人工核查（物理调用成本必须恰好等于分摊之和）。
        </div>
      )}

      <Card>
        <CardHeader className="flex-row items-center justify-between space-y-0">
          <div>
            <CardTitle>队列</CardTitle>
            <CardDescription>oldest pending age 是 P0 告警来源</CardDescription>
          </div>
          <Button variant="outline" size="sm" onClick={() => void refetch()} disabled={isFetching}>
            <RefreshCw className={isFetching ? 'h-3.5 w-3.5 animate-spin' : 'h-3.5 w-3.5'} aria-hidden /> 刷新
          </Button>
        </CardHeader>
        <CardContent>
          <div className="scroll-x">
            <table className="w-full min-w-[720px] text-sm">
              <caption className="sr-only">队列健康指标</caption>
              <thead className="border-b">
                <tr>
                  {['队列', '长度', 'Pending', '延迟中', '最旧未确认', '消费者', '死信'].map((h) => (
                    <th key={h} scope="col" className="px-2 py-2 text-left text-xs font-medium text-muted-foreground">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.queues.map((q) => (
                  <tr key={q.queue} className="border-b last:border-0">
                    <td className="px-2 py-2 font-mono text-xs">{q.queue}</td>
                    <td className="px-2 py-2 tabular text-xs">{formatNumber(q.length)}</td>
                    <td className="px-2 py-2 tabular text-xs">{formatNumber(q.pending)}</td>
                    <td className="px-2 py-2 tabular text-xs">{formatNumber(q.delayed)}</td>
                    <td className="px-2 py-2 text-xs">
                      <span className={q.oldest_pending_seconds > 300 ? 'font-medium text-destructive' : ''}>
                        {q.oldest_pending_seconds > 0 ? formatDuration(q.oldest_pending_seconds) : '—'}
                      </span>
                    </td>
                    <td className="px-2 py-2 tabular text-xs">{q.consumers}</td>
                    <td className="px-2 py-2">
                      {q.dead_letters > 0
                        ? <Badge variant="danger">{formatNumber(q.dead_letters)}</Badge>
                        : <span className="text-xs text-muted-foreground">0</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>调度层级</CardTitle>
            <CardDescription>状态新鲜度按层级统计</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {data.monitor_tiers.map((t) => (
                <div key={t.tier} className="flex items-center justify-between rounded-md border p-2.5 text-sm">
                  <span>{t.name || `L${t.tier}`}</span>
                  <span className="flex items-center gap-3 text-xs text-muted-foreground">
                    <span>{formatNumber(t.domains)} 个</span>
                    {t.overdue > 0 && <Badge variant="warning">逾期 {formatNumber(t.overdue)}</Badge>}
                    {t.freshness_p95_seconds != null && (
                      <Tooltip content="p95 状态新鲜度">
                        <span>p95 {formatDuration(t.freshness_p95_seconds)}</span>
                      </Tooltip>
                    )}
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>熔断状态</CardTitle>
            <CardDescription>持续 429/5xx 会触发熔断，半开探测后恢复</CardDescription>
          </CardHeader>
          <CardContent>
            {Object.keys(data.circuit_breakers).length === 0 ? (
              <EmptyState title="暂无熔断记录" />
            ) : (
              <div className="space-y-1.5">
                {Object.entries(data.circuit_breakers).map(([scope, state]) => (
                  <div key={scope} className="flex items-center justify-between rounded-md border p-2 text-xs">
                    <span className="truncate font-mono">{scope}</span>
                    <Badge variant={state === 'closed' ? 'success' : state === 'open' ? 'danger' : 'warning'}>
                      {{ closed: '正常', open: '已熔断', half_open: '半开探测' }[state] ?? state}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}

function DeadLetters() {
  const [queue, setQueue] = React.useState('')
  const { data, isLoading, error, refetch } = useDeadLetters(queue, true)
  const replay = useReplayDeadLetter()
  const toast = useToast()
  const [confirm, setConfirm] = React.useState<string | null>(null)

  if (error) return <ErrorState error={error} onRetry={() => void refetch()} />
  if (isLoading) return <LoadingState rows={4} />

  return (
    <>
      <div className="mb-3 flex flex-wrap gap-1">
        {['', 'domain.resolve', 'rule.scan', 'zone.snapshot', 'zone.diff', 'notification.send'].map((q) => (
          <Button key={q || 'all'} variant={queue === q ? 'default' : 'outline'} size="sm" onClick={() => setQueue(q)}>
            {q || '全部队列'}
          </Button>
        ))}
      </div>

      {(data?.items.length ?? 0) === 0 ? (
        <EmptyState icon={ShieldAlert} title="死信队列为空" description="所有任务都在正常重试范围内。" />
      ) : (
        <div className="space-y-2">
          {data!.items.map((dl) => (
            <Card key={dl.job_id}>
              <CardContent className="p-3">
                <div className="flex flex-wrap items-center gap-2">
                  <Badge variant="danger">{dl.error_class}</Badge>
                  <Badge variant="outline">{dl.queue}</Badge>
                  <span className="font-mono text-xs">{dl.job_type}</span>
                  <span className="text-xs text-muted-foreground">尝试 {dl.attempt} 次</span>
                  <Button
                    variant="outline" size="sm" className="ml-auto"
                    onClick={() => setConfirm(dl.job_id)}
                  >
                    <RotateCcw className="h-3.5 w-3.5" aria-hidden /> 重放
                  </Button>
                </div>
                <p className="mt-2 break-all text-xs text-destructive">{dl.error_message}</p>
                <p className="mt-1 font-mono text-[11px] text-muted-foreground">
                  job_id: {dl.job_id} · trace_id: {dl.trace_id}
                </p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* §12.1: DLQ replay requires a second confirmation and is audited. */}
      <Dialog open={Boolean(confirm)} onOpenChange={(o) => !o && setConfirm(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>重放该死信任务？</DialogTitle>
            <DialogDescription>
              重放会创建一个新任务并保留 original_job_id，再次经过权限与幂等校验。
              该操作会写入审计日志。
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirm(null)}>取消</Button>
            <Button
              onClick={async () => {
                if (!confirm) return
                try {
                  const res = await replay.mutateAsync(confirm)
                  toast.success('已重放', `新任务 ${res.new_job_id}`)
                } catch (err) {
                  toast.error('重放失败', err instanceof ApiError ? err.message : undefined)
                } finally {
                  setConfirm(null)
                }
              }}
            >
              确认重放
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}

/**
 * Danger zone: clears domains from the shared library.
 *
 * The shared library is master data (§8.1) — a purge cascades into other
 * users' rule matches and watchers. So the flow is: always preview first,
 * protect watched and matched domains unless explicitly included, and require
 * the confirmation phrase to be typed rather than a button to be clicked.
 * The endpoint itself refuses to exist in production.
 */
function PurgeDomains() {
  const preview = usePurgePreview()
  const purge = usePurgeDomains()
  const toast = useToast()

  const [scope, setScope] = React.useState<PurgeScope>({ source: 'generator' })
  const [confirm, setConfirm] = React.useState('')
  const [open, setOpen] = React.useState(false)

  const counts = preview.data?.preview

  async function runPreview(next: PurgeScope) {
    setScope(next)
    setConfirm('')
    try {
      await preview.mutateAsync(next)
    } catch (err) {
      toast.error('预览失败', err instanceof ApiError ? err.message : undefined)
    }
  }

  React.useEffect(() => {
    void runPreview(scope)
    // Run once on mount; later previews are triggered by the controls.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <Card className="border-destructive/40">
      <CardHeader>
        <CardTitle className="text-destructive">清空域名库（危险操作）</CardTitle>
        <CardDescription>
          域名库是全平台共享主数据，删除会级联清除相关的规则命中、监控与生命周期事件。
          该接口在生产环境不可用。usage_events 等计量事实为 append-only，不会被删除。
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap items-end gap-3">
          <div className="space-y-1.5">
            <Label htmlFor="purge-source" className="text-xs">来源</Label>
            <select
              id="purge-source"
              value={scope.source ?? ''}
              onChange={(e) => void runPreview({ ...scope, source: e.target.value || undefined })}
              className="h-8 rounded-md border border-input bg-background px-2 text-xs focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <option value="generator">规则生成（测试数据）</option>
              <option value="zone">Zone 观察</option>
              <option value="user_query">用户查询</option>
              <option value="rdap">RDAP 确认过的</option>
              <option value="">全部来源</option>
            </select>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="purge-tld" className="text-xs">后缀（可选）</Label>
            <Input
              id="purge-tld" className="h-8 w-28 text-xs" placeholder="如 xyz"
              defaultValue={scope.tld ?? ''}
              onBlur={(e) => void runPreview({ ...scope, tld: e.target.value || undefined })}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="purge-minlen" className="text-xs">最小长度</Label>
            <Input
              id="purge-minlen" type="number" min={0} className="h-8 w-20 text-xs"
              defaultValue={scope.min_label_length ?? ''}
              onBlur={(e) => void runPreview({ ...scope, min_label_length: Number(e.target.value) || undefined })}
            />
          </div>

          <label className="flex items-center gap-1.5 text-xs">
            <Checkbox
              checked={Boolean(scope.include_watched)}
              onCheckedChange={(v) => void runPreview({ ...scope, include_watched: Boolean(v) })}
            />
            含被监控的域名
          </label>
          <label className="flex items-center gap-1.5 text-xs">
            <Checkbox
              checked={Boolean(scope.include_matched)}
              onCheckedChange={(v) => void runPreview({ ...scope, include_matched: Boolean(v) })}
            />
            含已确认命中的域名
          </label>
        </div>

        {preview.isPending ? (
          <LoadingState rows={2} label="正在统计影响范围" />
        ) : counts ? (
          <div className="rounded-md border bg-muted/40 p-3">
            <p className="mb-2 text-xs font-medium">将删除</p>
            <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-xs sm:grid-cols-3">
              {[
                ['域名', counts.domains],
                ['生命周期事件', counts.domain_events],
                ['状态观察', counts.status_observations],
                ['规则命中', counts.rule_matches],
                ['监控', counts.watchers],
                ['价值分快照', counts.score_snapshots],
              ].map(([label, n]) => (
                <div key={String(label)} className="flex justify-between gap-2">
                  <span className="text-muted-foreground">{label}</span>
                  <span className={cn('tabular font-medium', Number(n) > 0 && 'text-destructive')}>
                    {formatNumber(Number(n))}
                  </span>
                </div>
              ))}
            </div>
            {counts.usage_events_preserved > 0 && (
              <p className="mt-2 text-[11px] text-muted-foreground">
                另有 {formatNumber(counts.usage_events_preserved)} 条用量计量记录将被保留（append-only）。
              </p>
            )}
            {preview.data?.notice && (
              <p className="mt-2 text-[11px] text-muted-foreground">{preview.data.notice}</p>
            )}
          </div>
        ) : null}

        <Button
          variant="destructive" size="sm"
          disabled={!counts || counts.domains === 0 || purge.isPending}
          onClick={() => { setConfirm(''); setOpen(true) }}
        >
          <Trash2 className="h-3.5 w-3.5" aria-hidden />
          删除 {counts ? formatNumber(counts.domains) : 0} 个域名
        </Button>
      </CardContent>

      <Dialog open={open} onOpenChange={(o) => !o && setOpen(false)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-destructive">确认删除？</DialogTitle>
            <DialogDescription>
              这将永久删除 {formatNumber(counts?.domains ?? 0)} 个域名及其
              {formatNumber(counts?.domain_events ?? 0)} 条生命周期事件、
              {formatNumber(counts?.rule_matches ?? 0)} 条规则命中和
              {formatNumber(counts?.watchers ?? 0)} 个监控。此操作不可撤销，并会写入审计日志。
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-1.5">
            <Label htmlFor="purge-confirm" className="text-xs">
              请输入 <code className="rounded bg-muted px-1 font-mono">DELETE</code> 以确认
            </Label>
            <Input
              id="purge-confirm" value={confirm} autoFocus
              onChange={(e) => setConfirm(e.target.value)}
              placeholder="DELETE"
            />
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>取消</Button>
            <Button
              variant="destructive"
              disabled={confirm !== 'DELETE' || purge.isPending}
              onClick={async () => {
                try {
                  const res = await purge.mutateAsync({ ...scope, confirm })
                  toast.success(
                    `已删除 ${formatNumber(res.result.domains)} 个域名`,
                    `耗时 ${res.result.duration}；${res.notice}`,
                  )
                  setOpen(false)
                  void runPreview(scope)
                } catch (err) {
                  toast.error('删除失败', err instanceof ApiError ? err.message : undefined)
                }
              }}
            >
              {purge.isPending ? '删除中…' : '确认删除'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  )
}
